import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Link,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Title } from 'react-admin';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { DocumentDetailResponse } from '../../../interfaces/document';
import { OpsReferral } from '../../../interfaces/referral';
import { getClinicsForSelect } from '../../../lib/clinic';
import { cohortLabel } from '../../../lib/referrals/cohorts';
import referralService from '../../../services/referral';
import { getEnaraUrl, getLeadDetailsUrl } from '../../../util/url';
import { openDocumentAsDownload, resolveDocumentFile } from '../../../utils/referralUpload.utils';
import { PageHeader } from '../../common/PageHeader/PageHeader';
import { TabPanel } from '../../common/TabPanel/TabPanel';
import '../../common/tab-section.css';
import { ReferralDocumentsTab } from '../../document/Details/tabs/ReferralDocumentsTab';
import { getReviewStatePresentation } from '../../document/shared';
import ReferralHistory from './ReferralHistory';
import { listSearchFor, REFERRALS_HUB_PATH } from './referralHubPaths';

const PANES = [
  { value: 'referral', label: 'Referral' },
  { value: 'history', label: 'Change history' },
] as const;

type DocumentFile = NonNullable<ReturnType<typeof resolveDocumentFile>>;

/**
 * The source document, beside the referral.
 *
 * Only a PDF is rendered inline. A referral package is a partner's ZIP or HL7 XML, and an XML
 * carrying an `<?xml-stylesheet?>` executes its XSLT when a browser renders it; leads-api
 * serves those as attachments and they open as a download, as on the document page.
 *
 * `file` is resolved once per document, not per fetch: a save or discard refetches the
 * document, and a re-signed `s3Key` in the iframe would re-download the PDF and lose the page.
 */
const DocumentPane: React.FC<{
  loaded: boolean;
  file: DocumentFile | null;
  failed: boolean;
}> = ({ loaded, file, failed }) => {
  if (failed) {
    return <Alert severity='error'>Could not load the source document.</Alert>;
  }
  if (!loaded) {
    return <Skeleton variant='rectangular' height='70vh' />;
  }

  if (!file) {
    return <Alert severity='info'>The source document has no file attached.</Alert>;
  }

  if (!file.isPdf) {
    return (
      <Alert
        severity='info'
        action={
          <Button
            color='inherit'
            size='small'
            startIcon={<DownloadIcon />}
            onClick={() => openDocumentAsDownload(file.url)}
          >
            Download file
          </Button>
        }
      >
        This referral arrived as a structured package (ZIP or XML), which opens as a download rather
        than a preview.
      </Alert>
    );
  }

  return (
    <Box
      component='iframe'
      title='Source document'
      src={file.url}
      sx={{ width: '100%', height: '75vh', border: 0, borderRadius: 1, bgcolor: 'grey.100' }}
    />
  );
};

/**
 * Why there is no card when this referral is not on its document. Until leads-api #125 the
 * document detail drops a discarded referral, so after a discard the refreshed document no
 * longer lists it; the referral itself still carries the date and the reason.
 */
const NotOnDocumentNotice: React.FC<{ referral: OpsReferral | null }> = ({ referral }) =>
  referral?.discardedAt ? (
    <Alert severity='info'>
      This referral was discarded on {new Date(referral.discardedAt).toLocaleString()}
      {referral.discardReason ? `: ${referral.discardReason}` : ''}. A discarded referral cannot be
      edited.
    </Alert>
  ) : (
    <Alert severity='warning'>
      This referral is not on its document, so it cannot be edited here. Open the document to see
      the referrals it lists.
    </Alert>
  );

/**
 * One referral next to its source document — the screen the brief's target state describes.
 *
 * The right pane is the document page's referral card, filtered to this referral, so editing,
 * saving, discarding and the propagation status behave exactly as they do there.
 */
const ReferralsHubDetail: React.FC = () => {
  const { referralId } = useParams<{ referralId: string }>();
  const history = useHistory();
  const location = useLocation();
  // Digits only: `Number()` alone also accepts `0x10`, `1e3` and `+5`.
  const id = /^\d+$/.test(referralId) ? Number(referralId) : NaN;

  const [referral, setReferral] = useState<OpsReferral | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [document, setDocument] = useState<DocumentDetailResponse | null>(null);
  const [documentFailed, setDocumentFailed] = useState(false);
  // A reload after a save or discard that failed. The page keeps what it last loaded rather than
  // replacing it with an error; the card keeps Edit off until a refreshed document arrives, since
  // an edit started from the pre-save values would revert the save.
  const [refreshFailed, setRefreshFailed] = useState(false);
  // The file shown beside the card, kept per document id; see `DocumentPane`.
  const [preview, setPreview] = useState<{ documentId: number; file: DocumentFile | null } | null>(
    null,
  );
  const [clinics, setClinics] = useState<Array<{ id: number; name: string }>>([]);
  const [pane, setPane] = useState<'referral' | 'history'>('referral');
  const paneIndex = PANES.findIndex(({ value }) => value === pane);
  // The legacy view loads the history only once asked, per referral.
  const [legacyHistoryFor, setLegacyHistoryFor] = useState<number | null>(null);
  // Bumped after a save or discard, so the history shows the entry that was just written.
  const [historyVersion, setHistoryVersion] = useState(0);

  // Back to the list as the operator left it, filters included. Opened from a list row, the list
  // is the previous history entry, so going back to it keeps browser Back from returning here;
  // opened any other way (a pasted link, a reload) there is no list to go back to, and the
  // unfiltered list is pushed. Where the page came from is kept in memory by the list
  // (`rememberOpenedFromList`), not in router state, which hash history drops.
  const backToList = () => {
    if (listSearchFor(location.pathname) !== null) {
      history.goBack();
    } else {
      history.push(REFERRALS_HUB_PATH);
    }
  };

  // The latest request wins, as in the list: a slow response for a referral or document the
  // operator already left must not overwrite the one on screen, or land after unmount.
  const referralRequest = useRef(0);
  const documentRequest = useRef(0);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  // Another referral starts from a blank page rather than the previous one's data. Declared
  // before the loads below so it runs first when `:referralId` changes.
  useEffect(() => {
    documentRequest.current += 1;
    setReferral(null);
    setNotFound(false);
    setLoadFailed(false);
    setDocument(null);
    setDocumentFailed(false);
    setPreview(null);
    setRefreshFailed(false);
    setPane('referral');
  }, [id]);

  // `refresh` is a reload of what is already on screen: a failure then keeps it.
  const loadReferral = useCallback(async (refresh = false) => {
    const current = ++referralRequest.current;
    const isCurrent = () => live.current && current === referralRequest.current;
    if (!refresh) {
      setNotFound(false);
      setLoadFailed(false);
    }
    try {
      const response = await referralService.getOpsReferral(id);
      if (isCurrent()) {
        setReferral(response);
      }
    } catch (error) {
      if (!isCurrent()) {
        return;
      }
      if (refresh) {
        setRefreshFailed(true);
      } else if (axios.isAxiosError(error) && error.response?.status === 404) {
        setNotFound(true);
      } else {
        setLoadFailed(true);
      }
    }
  }, [id]);

  const documentId = referral?.documentId ?? null;

  const loadDocument = useCallback(async (refresh = false) => {
    if (documentId === null) {
      return;
    }
    const current = ++documentRequest.current;
    if (!refresh) {
      setDocumentFailed(false);
    }
    const response = await referralService.getDocumentDetail(documentId).catch(() => null);
    if (!live.current || current !== documentRequest.current) {
      return;
    }
    if (response) {
      setDocument(response);
      // Recomputed only for another document, so a refresh does not reload the iframe.
      setPreview((previous) =>
        previous?.documentId === response.documentId
          ? previous
          : { documentId: response.documentId, file: resolveDocumentFile(response.s3Key) },
      );
    } else if (refresh) {
      setRefreshFailed(true);
    } else {
      setDocumentFailed(true);
    }
  }, [documentId]);

  useEffect(() => {
    if (Number.isInteger(id) && id > 0) {
      loadReferral();
    } else {
      referralRequest.current += 1;
      setNotFound(true);
    }
  }, [id, loadReferral]);

  useEffect(() => {
    loadDocument();
  }, [loadDocument]);

  useEffect(() => {
    getClinicsForSelect()
      .then((list) => setClinics(list.map(({ id: clinicId, name }) => ({ id: clinicId, name }))))
      .catch(() => setClinics([]));
  }, []);

  // After a save or a discard the card asks for fresh data; the summary above it (discarded,
  // data quality) comes from the referral, so both are reloaded.
  const handleReferralUpdate = useCallback(() => {
    setRefreshFailed(false);
    loadReferral(true);
    loadDocument(true);
    setHistoryVersion((version) => version + 1);
  }, [loadReferral, loadDocument]);

  const header = (
    <PageHeader
      showBackButton
      onBack={backToList}
      backButtonLabel='Back to Referrals'
      breadcrumbs={[
        { label: 'Referrals', onClick: backToList },
        { label: referral ? referral.patientName : `Referral ${referralId}`, active: true },
      ]}
    />
  );

  if (notFound || loadFailed) {
    return (
      <Box className='tab-section'>
        <Title title='Referral' />
        {header}
        <Alert severity={notFound ? 'warning' : 'error'}>
          {notFound
            ? `Referral ${referralId} was not found.`
            : 'Could not load this referral. Try again, or reload the page.'}
        </Alert>
      </Box>
    );
  }

  const review = getReviewStatePresentation(referral?.reviewState);
  // The card filters the document's referrals to this one; when it is not among them it would
  // say "No referral documents found", which reads as a document problem.
  const isOnDocument = Boolean(document?.referralDocuments?.some((r) => r.id === id));

  return (
    <Box className='tab-section'>
      <Title title='Referral' />
      {header}

      {!referral ? (
        <Skeleton variant='rectangular' height={56} sx={{ mb: 2 }} />
      ) : (
        <Paper
          variant='outlined'
          sx={{ p: 2, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <Typography variant='h6' sx={{ mr: 1 }}>
            {referral.patientName}
          </Typography>
          {/* Focusable, as in the list, so the explanations are reachable without a mouse. */}
          <Tooltip title={review.description}>
            <span tabIndex={0}>
              <Chip
                size='small'
                label={review.label}
                sx={{ color: '#fff', bgcolor: review.color, textTransform: 'capitalize' }}
              />
            </span>
          </Tooltip>
          <Chip size='small' variant='outlined' label={cohortLabel(referral.systemSource)} />
          {referral.discardedAt && (
            <Tooltip title={referral.discardReason ?? ''}>
              {/* A fixed label, as in the list: Sentry records a clicked element's aria-label,
                  and the reason is free text. A tab stop only when there is a reason to show. */}
              <span tabIndex={referral.discardReason ? 0 : undefined} aria-label='Discard reason'>
                <Chip
                  size='small'
                  variant='outlined'
                  label={`Discarded ${new Date(referral.discardedAt).toLocaleDateString()}`}
                />
              </span>
            </Tooltip>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {referral.crmId && (
            <Link
              href={getLeadDetailsUrl(referral.crmId)}
              target='_blank'
              rel='noopener noreferrer'
              variant='body2'
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              LeadSquared <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
          )}
          {referral.userId && (
            <Link
              href={getEnaraUrl(`/activeadmin/users/${referral.userId}`)}
              target='_blank'
              rel='noopener noreferrer'
              variant='body2'
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              Core account <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
          )}
          {referral.documentId !== null && (
            <Link
              component='button'
              variant='body2'
              onClick={() => history.push(`/documents/library/${referral.documentId}`)}
            >
              Document {referral.documentId}
            </Link>
          )}
        </Paper>
      )}

      {/*
        Neutral about what happened (a save or a discard) and which reload failed, since Edit
        follows the document's reload only. Not dismissible: while it shows, the card may be
        holding Edit off, and Retry is the way out.
      */}
      {refreshFailed && (
        <Alert
          severity='warning'
          sx={{ mb: 2 }}
          action={
            <Button color='inherit' size='small' onClick={handleReferralUpdate}>
              Retry
            </Button>
          }>
          This page could not reload the referral after the last change, so what is shown may be
          out of date. Retry to load the latest values before editing.
        </Alert>
      )}

      {referral && referral.documentId === null ? (
        // A legacy intake has no parsed document, and the card needs one to load from. Its
        // history is still worth reading.
        <Box>
          <Alert severity='info' sx={{ mb: 2 }}>
            This referral has no source document in the system, so there is nothing to show beside
            it and it cannot be edited here yet.
          </Alert>
          {/* The history shows superseded PHI, and leads-api does not yet record that it was
              read, so it loads only when asked for, as on the tab. */}
          {legacyHistoryFor === id ? (
            <ReferralHistory key={id} referralId={id} />
          ) : (
            <Button variant='outlined' size='small' onClick={() => setLegacyHistoryFor(id)}>
              Show change history
            </Button>
          )}
        </Box>
      ) : (
        // Stacked until `lg`: the card's inner Grids use viewport breakpoints, so beside the
        // document at `md` its `md={3}` insurance fields shrank to about 90-150px.
        <Grid container spacing={2}>
          <Grid item xs={12} lg={6}>
            <DocumentPane
              loaded={Boolean(document && preview)}
              file={preview?.file ?? null}
              failed={documentFailed}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <Tabs
              value={pane}
              onChange={(_event, value) => setPane(value)}
              aria-label='Referral and its change history'
              sx={{ mb: 1 }}
            >
              {PANES.map(({ value, label }, index) => (
                <Tab
                  key={value}
                  value={value}
                  label={label}
                  id={`tab-${index}`}
                  aria-controls={`tabpanel-${index}`}
                />
              ))}
            </Tabs>
            {/* Both panes stay mounted once opened. Unmounting the card on a tab switch cleared
                the refresh it schedules two seconds after a save or discard, and dropped any
                unsaved edit; the history, kept mounted, follows `historyVersion`. The history
                is fetched only when its tab is first opened. That keeps a draft across a tab
                switch, not across a data refresh: after a save the card keeps Edit off until
                the refreshed `document` arrives, so no edit starts from the pre-save values. */}
            <TabPanel value={paneIndex} index={0} sx={{ p: 0 }}>
              {document && !isOnDocument ? (
                <NotOnDocumentNotice referral={referral} />
              ) : document ? (
                <ReferralDocumentsTab
                  document={document}
                  clinics={clinics}
                  referralIds={[id]}
                  showHeader={false}
                  onReferralUpdate={handleReferralUpdate}
                />
              ) : (
                !documentFailed && <Skeleton variant='rectangular' height='70vh' />
              )}
            </TabPanel>
            {/* Keyed by referral: another one starts unopened, with an empty history. */}
            <TabPanel key={id} value={paneIndex} index={1} sx={{ p: 0 }}>
              <ReferralHistory referralId={id} refreshKey={historyVersion} />
            </TabPanel>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ReferralsHubDetail;
